import Toggle from 'components/common/Toggle';
import AccessPointTypes from 'models/types/AccessPointTypes';
import React from 'react';
import RoleSelector from './RoleSelector';

interface UserAccessPoint {
    accessPoint: AccessPointTypes;
    accessLevel: number;
    isActive: boolean;
}

interface Props {
    userAccessPoints: UserAccessPoint[];
    onAccessPointChange: (accessPoint: AccessPointTypes, isActive: boolean) => void;
    onBulkAccessPointChange?: (accessPoints: UserAccessPoint[]) => void;
    isReadOnly?: boolean;
}

const AccessPointsTab: React.FC<Props> = ({
    userAccessPoints,
    onAccessPointChange,
    onBulkAccessPointChange,
    isReadOnly = false,
}) => {
    const isAccessPointActive = (accessPoint: AccessPointTypes): boolean => {
        const userAccess = userAccessPoints.find((ua) => ua.accessPoint === accessPoint);
        return userAccess?.isActive || false;
    };

    const getAccessLevel = (accessPoint: AccessPointTypes): number => {
        const userAccess = userAccessPoints.find((ua) => ua.accessPoint === accessPoint);
        return userAccess?.accessLevel || 1;
    };

    const getAccessLevelText = (level: number): string => {
        switch (level) {
            case 1:
                return 'Read';
            case 2:
                return 'Write';
            case 3:
                return 'Delete';
            default:
                return 'None';
        }
    };

    const handleToggleChange = (accessPoint: AccessPointTypes, newValue: boolean) => {
        if (!isReadOnly) {
            onAccessPointChange(accessPoint, newValue);
        }
    };

    const handleRoleSelect = (roleAccessPoints: UserAccessPoint[]) => {
        if (!isReadOnly && onBulkAccessPointChange) {
            onBulkAccessPointChange(roleAccessPoints);
        }
    };

    return (
        <div className="p-4">
            {!isReadOnly && onBulkAccessPointChange && (
                <RoleSelector onRoleSelect={handleRoleSelect} disabled={isReadOnly} />
            )}

            <div className="space-y-3">
                <div className="text-sm font-medium text-gray-700 mb-3">Individual Access Points</div>
                {accessPointTypes.map((AP) => {
                    const isActive = isAccessPointActive(AP.value);
                    const accessLevel = getAccessLevel(AP.value);

                    return (
                        <div key={AP.key} className="flex items-center justify-between py-2 border-b border-gray-100">
                            <div className="flex-1">
                                <div className="font-semibold text-gray-700">{AP.text}</div>
                                {isActive && (
                                    <div className="text-sm text-gray-500">
                                        Access Level: {getAccessLevelText(accessLevel)}
                                    </div>
                                )}
                            </div>
                            <div className="ml-auto">
                                <Toggle
                                    value={isActive}
                                    name={AP.text}
                                    onChange={(newValue: boolean) => handleToggleChange(AP.value, newValue)}
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            {isReadOnly && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                        You cannot modify your own access permissions. Contact an administrator if changes are needed.
                    </p>
                </div>
            )}
        </div>
    );
};

const accessPointTypes = [
    { value: AccessPointTypes.Dashboard, key: AccessPointTypes.Dashboard, text: 'Dashboard' },
    { value: AccessPointTypes.Calendar, key: AccessPointTypes.Calendar, text: 'Calendar' },
    { value: AccessPointTypes.Inventory, key: AccessPointTypes.Inventory, text: 'Inventory' },
    { value: AccessPointTypes.Reports, key: AccessPointTypes.Reports, text: 'Reports' },
    { value: AccessPointTypes.Settings, key: AccessPointTypes.Settings, text: 'Settings' },
];

export default AccessPointsTab;
